import { Webapi } from '@/Webapi'
import {
    subscribeToDriverPosition,
    subscribeToParcelLifecycleUpdates,
    unsubscribe,
} from '@/Webstomp'
import RouteMap from './RouteMap'
import TaskList from './TaskList'
import SplitRouteModal from '@/widgets/SplitRouteModal'
import CancelModal from '@/views/RouteOverview/CancelModal'
import CompleteParcelsAtStopModal from '@/widgets/CompleteParcelsAtStopModal'
import { humanTime } from '@/utils/time/human'

const getEmptyDriver = () => ({
    id: '',
    name: '',
    phoneNumber: '',
    courier: '',
})

const taskBySequencePredicate = (sequence) => (task) => task.sequence === sequence

export default {
    name: 'RouteOverview',
    components: {
        RouteMap,
        TaskList,
        SplitRouteModal,
        CancelModal,
        CompleteParcelsAtStopModal,
    },
    data: () => ({
        tasks: null,
        routeError: false,
        mapdata: [],
        filterQuery: '',
        selectedTaskIds: [],
        expandedTaskId: null,
        routeId: null,
        uiData: null,
        driverPosition: null,
        lastInternalComment: '',
        subscription: null,
        parcelLifecycleSubscription: null,
        driver: getEmptyDriver(),
        isCancelModalOpen: false,
        isSplitRouteOpen: false,
        isCompleteModalOpen: false,
        returnPallets: [],
        pickupHistory: null,
    }),
    computed: {
        positionTimeDiff() {
            const { driverPosition } = this.$data
            if (!driverPosition) {
                return 'No Position Yet Received'
            }
            return `Last Received Position: ${humanTime(driverPosition.secondsDiff)} ago`
        },
        positionTimeDiffCss() {
            const { driverPosition } = this.$data
            if (!driverPosition) {
                return 'tag is-danger'
            }
            if (driverPosition.secondsDiff > 5 * 60) {
                return 'tag is-danger'
            }
            if (driverPosition.secondsDiff > 1 * 60) {
                return 'tag is-warning'
            }
            return 'tag'
        },
        selectedTasks() {
            return this.$data.tasks.filter((task) => this.$data.selectedTaskIds.includes(task.id))
        },
        selectedParcelsToComplete() {
            return this.selectedTasks
                .map((task) => task.parcels)
                .flat()
                .filter((parcel) => !parcel.completed)
        },
        cancelDisabled() {
            if (this.selectedTasks.length === 0) {
                return true
            }
            return (
                this.selectedTasks.filter((task) => task.cancelable).length !==
                this.selectedTasks.length
            )
        },
        splitDisabled() {
            if (this.selectedTasks.length === 0) {
                return true
            }
            return (
                this.selectedTasks.filter((task) => task.splittable).length !==
                this.selectedTasks.length
            )
        },
        completedDisabled() {
            if (this.selectedTasks.length !== 1) {
                return true
            }
            return !this.selectedTasks[0].completable
        },
        selectedStopMarker() {
            if (this.expandedTaskId) {
                const expandedTask = this.tasks.find((task) => task.id === this.expandedTaskId)

                return expandedTask
                    ? this.mapdata.find((stop) => stop.sequence === expandedTask.sequence)
                    : null
            }

            return null
        },
        loaded() {
            return this.$data.tasks !== null
        },
    },
    methods: {
        onTaskExpanded(taskId) {
            this.expandedTaskId = this.expandedTaskId === taskId ? null : taskId
        },
        onMarkerClick(marker) {
            return () => {
                const task = this.tasks.find(taskBySequencePredicate(marker.sequence))

                if (task) {
                    this.expandedTaskId = task.id
                }
            }
        },
        urlChange({ origin, data }) {
            // if data is not string, then its a webpack auto reload
            if (typeof data !== 'string') {
                return
            }

            // in case if url update is not for a route
            if (!data.startsWith('/routes/')) {
                return
            }
            const {
                location: { protocol, host },
            } = window
            if (origin !== `${protocol}//${host}`) {
                return
            }

            if (this.$route.path !== data) {
                this.$router.push(data)
            }
            this.reset(this.$route.params.id)
            this.fetchRoute(true)
        },
        onTasksSelectedChange(taskIds) {
            this.selectedTaskIds = taskIds
        },
        unsubscribeDriverPosition() {
            unsubscribe(this.subscription.subscriptionId)
            this.$data.subscription = null
        },
        unsubscribeParcelLifeCycleUpdates() {
            unsubscribe(this.parcelLifecycleSubscription.subscriptionId)
            this.$data.parcelLifecycleSubscription = null
        },
        reset(routeId) {
            this.routeId = parseInt(routeId, 10)
            this.tasks = null
            this.mapdata = []
            this.selectedTaskIds = []
            this.$data.routeError = false
        },
        resync() {
            this.fetchRoute(false)
        },
        fetchRoute(resetSelectedTasks = false) {
            Webapi()
                .route(this.routeId)
                .then((json) => {
                    this.tasks = json.payload.tasks.sort((a, b) => a.sequence - b.sequence)
                    this.mapdata = json.payload.stopMarkers
                    this.driver = json.payload.driver
                    this.lastInternalComment = json.payload.lastInternalComment || ''
                    if (resetSelectedTasks) {
                        this.selectedTaskIds = []
                    } else {
                        const findTaskById = (taskId) =>
                            this.tasks.filter((task) => task.id === taskId)[0] || {
                                splittable: false,
                                cancelable: false,
                                completable: false,
                            }

                        const canTaskBeSelectable = (task) =>
                            task.splittable || task.cancelable || task.completable

                        this.selectedTaskIds = this.selectedTaskIds.filter((taskId) =>
                            canTaskBeSelectable(findTaskById(taskId)),
                        )
                    }

                    if (json.payload.routeType === 'MERCHANT') {
                        this.fetchMerchantRouteDetails(this.routeId, this.driver.id)
                    }

                    if (
                        this.subscription !== null &&
                        this.subscription.driverId !== this.driver.id
                    ) {
                        this.unsubscribeDriverPosition()
                    }

                    if (json.payload.lastKnownDriverPosition) {
                        this.onDriverPositionUpdated(json.payload.lastKnownDriverPosition)
                    } else {
                        this.driverPosition = null
                    }

                    subscribeToDriverPosition(this.driver.id, this.onDriverPositionUpdated).then(
                        (subscriptionId) => {
                            this.$data.subscription = {
                                driverId: this.driver.id,
                                subscriptionId,
                            }
                        },
                    )
                    this.requestParcelLifecycleUpdates()
                })
                .catch((e) => {
                    this.driver = getEmptyDriver()
                    this.reset(this.$route.params.id)
                    this.$data.routeError = true
                    console.log(e)
                })
        },
        requestParcelLifecycleUpdates() {
            if (
                !this.parcelLifecycleSubscription ||
                this.parcelLifecycleSubscription.routeId !== this.routeId
            ) {
                if (this.parcelLifecycleSubscription) {
                    this.unsubscribeParcelLifeCycleUpdates()
                }
                subscribeToParcelLifecycleUpdates(this.routeId, this.resync).then(
                    (subscriptionId) => {
                        this.parcelLifecycleSubscription = {
                            routeId: this.routeId,
                            subscriptionId,
                        }
                    },
                )
            }
        },
        onDriverPositionUpdated(position) {
            if (this.driverPosition && this.driverPosition.intervalId) {
                clearInterval(this.driverPosition.intervalId)
            }
            const updatePositionTimeDiff = () => {
                if (this.driverPosition == null) {
                    return
                }
                const diff = new Date() - new Date(this.driverPosition.date)
                const secondsDiff = Math.floor(diff / 1000)
                this.driverPosition = {
                    ...this.driverPosition,
                    secondsDiff,
                }
            }
            const intervalId = setInterval(updatePositionTimeDiff, 60 * 1000)
            this.driverPosition = {
                date: position.date,
                coordinate: position.coordinate,
                intervalId,
            }
            updatePositionTimeDiff()
        },
        onCancelClick() {
            if (!this.cancelDisabled) {
                this.isCancelModalOpen = true
            }
        },
        onCancelClose() {
            this.isCancelModalOpen = false
        },
        onSplitClick() {
            if (!this.splitDisabled) {
                this.isSplitRouteOpen = true
            }
        },
        onSplitClose() {
            this.isSplitRouteOpen = false
        },
        onCompleteClick() {
            if (!this.completedDisabled) {
                this.isCompleteModalOpen = true
            }
        },
        onCompleteClose() {
            this.isCompleteModalOpen = false
        },
        openComments() {
            window.open(`/routes/${this.$data.routeId}/comments`, 'Comments', '_blank')
        },
        fetchMerchantRouteDetails(routeId, driverId) {
            Webapi()
                .pickupHistory(routeId)
                .then((res) => {
                    this.pickupHistory = res.payload
                })
                .catch((err) => console.error(err))
            Webapi()
                .returnPallets(routeId, driverId)
                .then((res) => {
                    this.returnPallets = res.payload
                })
                .catch((err) => console.error(err))
        },
    },
    beforeMount() {
        window.addEventListener('message', this.urlChange)
        this.routeId = parseInt(this.$route.params.id, 10)
        Webapi()
            .uiData()
            .then((json) => {
                this.uiData = json
            })
        this.fetchRoute(false)
    },
    beforeDestroy() {
        window.removeEventListener('message', this.urlChange)
    },
}
